import { useMemo, useCallback } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type XAxisFormat = 'day' | 'month' | 'year'

/**
 * Custom hook to determine x-axis tick format based on time range
 * @param data Array of data points with date and timestamp properties
 * @returns Object containing formatter function, config, and calculated ticks
 */
export const useIntervalXAxisFormatter = (
    data: (Record<string, unknown> & { date: string; timestamp?: number })[]
) => {
    // Determine format based on time range
    const xAxisFormat = useMemo<XAxisFormat>(() => {
        if (data.length === 0) {
            return 'day'
        }

        // Parse timestamp strings (can be ISO strings with timezone or UTC strings)
        const firstDate = dayjs.utc(data[0].date)
        const lastDate = dayjs.utc(data[data.length - 1].date)
        const daysDiff = lastDate.diff(firstDate, 'day')

        // Less than 60 days: show day format
        if (daysDiff < 60) {
            return 'day'
        }
        // 60-365 days: show months
        else if (daysDiff < 365) {
            return 'month'
        }
        // More than 365 days: show years
        else {
            return 'year'
        }
    }, [data])

    const formatDateForXAxis = useCallback(
        (value: string | number, _index?: number) => {
            // Parse value - can be timestamp (number) or date string
            const date = typeof value === 'number' ? dayjs(value) : dayjs(value)

            if (xAxisFormat === 'year') {
                return date.format('YYYY')
            } else if (xAxisFormat === 'month') {
                return date.format('MMM YYYY')
            } else {
                // Day format
                return date.format('D MMM')
            }
        },
        [xAxisFormat]
    )

    // Calculate tick positions at natural time boundaries
    const xAxisTicks = useMemo((): number[] => {
        if (data.length === 0) {
            return []
        }

        const firstDate = dayjs.utc(data[0].date)
        const lastDate = dayjs.utc(data[data.length - 1].date)
        const ticks: number[] = []

        if (xAxisFormat === 'year') {
            // Start of each year
            let current = firstDate.startOf('year')
            while (
                current.isBefore(lastDate) ||
                current.isSame(lastDate, 'year')
            ) {
                ticks.push(current.valueOf())
                current = current.add(1, 'year')
            }
        } else if (xAxisFormat === 'month') {
            // Start of each month
            let current = firstDate.startOf('month')
            while (
                current.isBefore(lastDate) ||
                current.isSame(lastDate, 'month')
            ) {
                ticks.push(current.valueOf())
                current = current.add(1, 'month')
            }
        } else {
            // Day format - calculate appropriate interval
            const daysDiff = lastDate.diff(firstDate, 'day')
            const targetTickCount = 6
            const dayInterval = Math.max(
                1,
                Math.ceil(daysDiff / targetTickCount)
            )

            let current = firstDate.startOf('day')
            while (
                current.isBefore(lastDate) ||
                current.isSame(lastDate, 'day')
            ) {
                ticks.push(current.valueOf())
                current = current.add(dayInterval, 'day')
            }
        }

        return ticks
    }, [data, xAxisFormat])

    return {
        xAxisTickFormatter: formatDateForXAxis,
        xAxisFormat,
        xAxisTicks,
    }
}
